'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.wrap = wrap;
exports.wrapMethod = wrapMethod;
exports.autobind = autobind;
exports.memoize = memoize;
exports.getDisplayName = getDisplayName;

var _immutable = require('immutable');

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/*
 * Standardized call to wrap a function add add some meta data
 */
function wrap(original, callback) {
  callback._wrapped = original;
  return callback;
}

/*
 * Standardize call to wrap a class method
 */
function wrapMethod(target, key, descriptor, callback) {
  var original = descriptor.value;
  return {
    configurable: true,
    get: function get() {
      if (this === target.prototype || this.hasOwnProperty(key)) {
        return original;
      }

      // Bind "this"  -- See:
      var boundCb = callback.bind(this);
      Object.defineProperty(this, key, {
        value: boundCb,
        configurable: true,
        writable: true
      });
      return boundCb;
    }
  };
}

/*
 * Bind a method to this for use in callbacks
 */
function autobind(target, key, descriptor) {
  var fn = descriptor.value;
  return {
    configurable: true,
    get: function get() {
      if (this === target.prototype || this.hasOwnProperty(key)) {
        return fn;
      }

      // Bind "this"  -- See:
      var boundCb = fn.bind(this);
      Object.defineProperty(this, key, {
        value: boundCb,
        configurable: true,
        writable: true
      });
      return boundCb;
    }
  };
}

/*
 * Bind a method and then also memoize its results
 */
function memoize(target, key, descriptor) {
  var fn = descriptor.value;
  if (fn !== void 0) {
    return _memoizeMethod(target, key, descriptor);
  } else {
    return _memoizeProperty(target, key, descriptor);
  }
}

/*
 * Function for producing a compound key of function arguments to be used for memoizing functions
 */
function _compoundKey(args) {
  var result = [];
  args.forEach(function (arg) {
    if (typeof arg === 'number' || typeof arg === 'string' || typeof arg === 'boolean') {
      result.push(arg);
    } else if (Array.isArray(arg)) {
      result.push(JSON.stringify(arg));
    } else {
      // Assume object if not number, string, boolean, or array.
      result.push(JSON.stringify(arg));
    }
  });
  return result;
}

/*
 * Memoize the result of a method call using a WeakMap on the object instance as the basis for the eviction of
 * the cache.
 */
function _memoizeMethod(target, key, descriptor) {
  var fn = descriptor.value;
  return {
    configurable: true,
    get: function get() {
      if (this === target.prototype || this.hasOwnProperty(key)) {
        return fn;
      }

      // Create memoize function that is customized to the behaviour of a method call with arguments as keys
      function _memoize(func, instance, cacheKey) {
        var slice = Array.prototype.slice;
        return function _memoizeInner() {
          var cacheMap = instance._memoized;
          if (cacheMap === void 0) {
            cacheMap = (0, _immutable.Map)();
          }

          var args = slice.call(arguments);
          var argsCacheKey = JSON.stringify(args);

          var result = void 0;

          // Return cached result or build the new result.  Intentionally uses hasIn( ... ) to avoid having to set a
          // sentinel value.
          if (cacheMap.hasIn([cacheKey, argsCacheKey])) {
            result = cacheMap.getIn([cacheKey, argsCacheKey]);
          } else {
            // Run function
            result = func.apply(instance, args);

            // Save result
            if (!cacheMap.has(cacheKey)) {
              cacheMap = cacheMap.set(cacheKey, (0, _immutable.Map)(_defineProperty({}, argsCacheKey, result)));
            } else {
              cacheMap = cacheMap.setIn([cacheKey, argsCacheKey], result);
            }
            instance._memoized = cacheMap;
          }
          return result;
        };
      }

      // Replace the method with the new memoized one
      var boundMemoizeCb = _memoize(fn, this, key);
      Object.defineProperty(this, key, {
        value: boundMemoizeCb,
        configurable: true,
        writable: true
      });
      return boundMemoizeCb;
    }
  };
}

/*
 * Memoize the result of a property using a WeakMap on the object instance as the basis for the eviction of
 * the cache.
 */
function _memoizeProperty(target, key, descriptor) {

  // Create memoize function that is customized to the behaviour of a property
  function _memoize(func, cacheKey) {
    return function _memoizeInner() {
      var instance = this;
      var cacheMap = instance._memoized;
      if (cacheMap === void 0) {
        cacheMap = (0, _immutable.Map)();
      }

      var result = void 0;
      if (cacheMap.has(cacheKey)) {
        result = cacheMap.get(cacheKey);
      } else {
        result = func.apply(instance);
        cacheMap = cacheMap.set(cacheKey, result);
        instance._memoized = cacheMap;
      }
      return result;
    };
  }

  // Replace the property with the new memoized one
  descriptor.get = _memoize(descriptor.get, key);
  return descriptor;
}

/*
 * Determine the name of a wrapped class
 */
function getDisplayName(WrappedClass) {
  return WrappedClass.displayName || WrappedClass.name || 'Class';
}